import {
  Container,
  Grid,
  Table,
  TableBody,
  TableContainer,
  Typography,
} from '@mui/material'
import type { FC } from 'react'
import { useCallback, useEffect, useState } from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'

import InfiniteScrollLoading from '@/components/UI/InfiniteScrollLoading'
import AppBarPageContainer from '@/components/UI/PageContainer'
import PageContentStickyTableHeader from '@/components/UI/PageContentStickyTableHeader'
import PaperedGridItem from '@/components/UI/PaperedGridItem'
import Error from '@/components/UI/Table/Error'
import NoResults from '@/components/UI/Table/NoResults'
import TableRowLoading from '@/components/UI/Table/RowLoading'
import useUser from '@/hooks/useUser'
import { getExecutivesQuery } from '@/lib/apollo'
import type { TransformedExecutiveData } from '@/lib/apollo/query/getExecutives'
import { Role } from '@/types/User'
import { hasRole } from '@/utils/role'
import { defaultGridSpacing } from '@/utils/themes'
import UnauthorisedPage from '@/views/UnauthorisedPage'

import Row from './Row'
import TableHeader from './TableHeader'

export type Props = {
  collapsedMenu: boolean
}

export const allowedRoles = [Role.admin, Role.supervisor]

const ExecutivesPage: FC<Props> = ({ collapsedMenu }) => {
  const [infiniteScrollData, setInfiniteScrollData] = useState<
    TransformedExecutiveData[]
  >([])
  const [totalResults, setTotalResults] = useState<number>(0)

  const { customerId, roles } = useUser()

  const hasPermissionToViewPage = hasRole(roles, allowedRoles)

  const [fetch, { loading, error }] = getExecutivesQuery.useLazyQuery({
    onCompleted: data => {
      const transformedData = getExecutivesQuery.toTransformedData(data)

      if (transformedData) {
        setTotalResults(transformedData.total)
        setInfiniteScrollData([...infiniteScrollData, ...transformedData.results])
      }
    },
  })

  const getNextPage = useCallback(() => {
    if (!customerId) {
      return
    }

    fetch({
      variables: {
        customerId,
        limit: 200,
        offset: infiniteScrollData.length,
      },
    })
  }, [fetch, customerId, infiniteScrollData.length])

  useEffect(() => {
    if (infiniteScrollData.length === 0) {
      getNextPage()
    }
  }, [getNextPage, customerId, infiniteScrollData.length])

  return !hasPermissionToViewPage ? (
    <UnauthorisedPage />
  ) : (
    <AppBarPageContainer
      isNavigationDrawerOpen={!collapsedMenu}
      headerAppBarContent={
        <Typography component="h1" variant="h6">
          Executives
        </Typography>
      }
      pageContent={
        <>
          <PageContentStickyTableHeader
            isNavigationDrawerOpen={!collapsedMenu}
            yOffset={28}
          >
            <TableHeader />
          </PageContentStickyTableHeader>
          <Container maxWidth="xl">
            <Grid
              container
              paddingTop={defaultGridSpacing}
              paddingBottom={defaultGridSpacing}
              spacing={defaultGridSpacing}
            >
              <PaperedGridItem xs={12}>
                <InfiniteScroll
                  dataLength={infiniteScrollData.length}
                  next={getNextPage}
                  hasMore={infiniteScrollData.length < totalResults}
                  loader={<>{!error && <InfiniteScrollLoading />}</>}
                  style={{ overflowY: 'hidden' }}
                >
                  <TableContainer>
                    <Table aria-label="simple table" size="small">
                      <TableHeader />
                      <TableBody>
                        <>
                          {infiniteScrollData.length === 0 && loading ? (
                            <TableRowLoading colSpan={4} />
                          ) : !error && infiniteScrollData.length === 0 ? (
                            <NoResults colSpan={4} />
                          ) : (
                            infiniteScrollData.map(executive => (
                              <Row executive={executive} />
                            ))
                          )}
                          {error && <Error colSpan={4} />}
                        </>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </InfiniteScroll>
              </PaperedGridItem>
            </Grid>
          </Container>
        </>
      }
    />
  )
}

export default ExecutivesPage
